import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Copy, ExternalLink, Share2, BarChart3, Eye, CheckCircle2, QrCode, Code2, Mail, Facebook, MessageCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtDateTime } from "../../lib/api";
import { Modal, StatCard, inputCls, F, fmtINR } from "./CrmShared";
import CrmFormBuilder from "./CrmFormBuilder";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const STATUS_STYLE = {
  published: "bg-emerald-50 text-emerald-700 border-emerald-200",
  draft: "bg-amber-50 text-amber-700 border-amber-200",
  archived: "bg-muted text-muted-foreground border-border",
};

export function formLink(f) {
  return `${window.location.origin}/forms/${f.public_id}`;
}

export default function CrmForms({ refreshKey }) {
  const [forms, setForms] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [shareForm, setShareForm] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(() => {
    api.get("/store-admin/crm/forms").then((r) => setForms(r.data.forms)).catch((e) => toast.error(errMsg(e)));
    api.get("/store-admin/crm/pipelines").then((r) => setPipelines(r.data.pipelines)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const pipeName = (id) => pipelines.find((p) => p.id === id)?.name || "Sales Pipeline";
  const copy = (text, what = "Link") => { navigator.clipboard.writeText(text); toast.success(`${what} copied`); };

  const setStatus = async (f, status) => {
    try { await api.post(`/store-admin/crm/forms/${f.id}/status`, { status }); toast.success(status === "published" ? "Form published — link is live" : `Form ${status}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const dup = async (f) => {
    try { await api.post(`/store-admin/crm/forms/${f.id}/duplicate`); toast.success("Form duplicated"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (f) => {
    if (!window.confirm(`Delete form "${f.name}"? Leads already captured stay in CRM.`)) return;
    try { await api.delete(`/store-admin/crm/forms/${f.id}`); toast.success("Form deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openAnalytics = async (f) => {
    try { const r = await api.get(`/store-admin/crm/forms/${f.id}/analytics`); setAnalytics({ ...r.data, form: f }); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const iconBtn = "h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:bg-muted shrink-0";

  return (
    <div className="space-y-5" data-testid="crm-forms">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-lg">Lead Capture Forms</h2>
          <p className="text-xs text-muted-foreground">Build custom forms, share the public link anywhere, leads land in CRM automatically.</p>
        </div>
        <button onClick={() => { setEditForm(null); setBuilderOpen(true); }} data-testid="forms-create"
          className="h-10 px-5 rounded-full text-white text-xs font-extrabold flex items-center gap-1.5"
          style={{ backgroundImage: "linear-gradient(135deg, #059669, #0d9488)" }}>
          <Plus className="h-3.5 w-3.5" /> Create New Form
        </button>
      </div>

      {forms === null ? <div className="h-32 flex items-center justify-center"><div className="h-7 w-7 rounded-full border-2 border-border border-t-foreground animate-spin" /></div>
        : forms.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="forms-empty">
            <FileText className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No forms yet. Create your first lead capture form.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {forms.map((f) => (
              <div key={f.id} className="rounded-2xl border border-border bg-background p-5 space-y-3" data-testid={`form-card-${f.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-sm truncate">{f.name}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{f.fields.length} fields · {pipeName(f.pipeline_id)} → {f.default_stage}</p>
                  </div>
                  <span className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide border ${STATUS_STYLE[f.status]}`} data-testid={`form-status-${f.id}`}>{f.status}</span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  {[["Views", f.views], ["Submissions", f.submissions], ["Leads", f.leads_count], ["Conv.", `${f.conversion}%`]].map(([l, v]) => (
                    <div key={l} className="rounded-xl bg-muted/50 py-2"><p className="font-extrabold text-sm">{v}</p><p className="text-[9px] uppercase tracking-wide font-bold text-muted-foreground">{l}</p></div>
                  ))}
                </div>
                {f.status === "published" && (
                  <div className="flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-2" data-testid={`form-link-${f.id}`}>
                    <span className="text-[11px] font-mono truncate flex-1 text-emerald-800">{formLink(f)}</span>
                    <button onClick={() => copy(formLink(f))} className="h-7 w-7 rounded-lg hover:bg-white flex items-center justify-center shrink-0" title="Copy link" data-testid={`form-copy-${f.id}`}><Copy className="h-3.5 w-3.5" /></button>
                    <a href={formLink(f)} target="_blank" rel="noreferrer" className="h-7 w-7 rounded-lg hover:bg-white flex items-center justify-center shrink-0" title="Open form" data-testid={`form-open-${f.id}`}><ExternalLink className="h-3.5 w-3.5" /></a>
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5 pt-1">
                  <button onClick={() => { setEditForm(f); setBuilderOpen(true); }} className={iconBtn} title="Edit" data-testid={`form-edit-${f.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  {f.status === "published" && <a href={formLink(f)} target="_blank" rel="noreferrer" className={iconBtn} title="Preview" data-testid={`form-preview-${f.id}`}><Eye className="h-3.5 w-3.5" /></a>}
                  <button onClick={() => setShareForm(f)} className={iconBtn} title="Share" data-testid={`form-share-${f.id}`}><Share2 className="h-3.5 w-3.5" /></button>
                  <button onClick={() => openAnalytics(f)} className={iconBtn} title="Analytics" data-testid={`form-analytics-${f.id}`}><BarChart3 className="h-3.5 w-3.5" /></button>
                  <button onClick={() => dup(f)} className={iconBtn} title="Duplicate" data-testid={`form-dup-${f.id}`}><Copy className="h-3.5 w-3.5" /></button>
                  {f.status !== "published"
                    ? <button onClick={() => setStatus(f, "published")} className="h-8 px-3 rounded-lg bg-emerald-600 text-white text-[11px] font-extrabold" data-testid={`form-publish-${f.id}`}>Publish</button>
                    : <button onClick={() => setStatus(f, "draft")} className="h-8 px-3 rounded-lg border border-border text-[11px] font-extrabold hover:bg-muted" data-testid={`form-unpublish-${f.id}`}>Unpublish</button>}
                  <button onClick={() => del(f)} className={`${iconBtn} hover:bg-red-50 hover:text-red-600`} title="Delete" data-testid={`form-del-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                <p className="text-[10px] text-muted-foreground">Created {fmtDateTime(f.created_at)}{f.last_submission ? ` · Last submission ${fmtDateTime(f.last_submission)}` : ""}</p>
              </div>
            ))}
          </div>
        )}

      {/* Facebook Ads usage instructions (honest — no false Instant Form claims) */}
      <div className="rounded-2xl border border-border bg-background p-5" data-testid="fb-ads-usage">
        <h3 className="font-display font-bold flex items-center gap-2"><Facebook className="h-4 w-4 text-blue-600" /> Use Your Form in Facebook Ads</h3>
        <ol className="mt-3 space-y-1.5 text-sm text-muted-foreground list-decimal list-inside">
          <li>Copy your published CRM form link.</li>
          <li>Open Meta Ads Manager.</li>
          <li>Create your Lead / Traffic campaign per your Meta setup.</li>
          <li>Use the MILTROO CRM form link as the destination URL where supported.</li>
          <li>Submissions automatically become CRM leads with source tracking.</li>
        </ol>
        <p className="text-[11px] text-muted-foreground mt-3">Native Meta Instant Form sync is available separately under CRM → Facebook Leads.</p>
      </div>

      <CrmFormBuilder open={builderOpen} onClose={() => setBuilderOpen(false)} form={editForm} pipelines={pipelines} onSaved={load} />

      {/* Share modal */}
      <Modal open={!!shareForm} onClose={() => setShareForm(null)} title={`Share — ${shareForm?.name || ""}`} testid="form-share-modal">
        {shareForm && (
          <div className="space-y-3">
            {shareForm.status !== "published" && <p className="text-xs font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">Publish the form first to make its link live.</p>}
            <div className="rounded-xl bg-muted/50 px-3 py-2.5 font-mono text-xs break-all" data-testid="share-link-text">{formLink(shareForm)}</div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => copy(formLink(shareForm))} data-testid="share-copy" className="h-11 rounded-xl border border-border text-xs font-extrabold flex items-center justify-center gap-1.5 hover:bg-muted"><Copy className="h-3.5 w-3.5" /> Copy Link</button>
              <a data-testid="share-whatsapp" target="_blank" rel="noreferrer"
                href={`https://wa.me/?text=${encodeURIComponent((shareForm.whatsapp_share_msg || "Hello! Please fill out this form to share your details with us:") + "\n" + formLink(shareForm))}`}
                className="h-11 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-extrabold flex items-center justify-center gap-1.5"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
              <a data-testid="share-facebook" target="_blank" rel="noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(formLink(shareForm))}`}
                className="h-11 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 text-xs font-extrabold flex items-center justify-center gap-1.5"><Facebook className="h-3.5 w-3.5" /> Facebook</a>
              <a data-testid="share-email" href={`mailto:?subject=${encodeURIComponent(shareForm.name)}&body=${encodeURIComponent(formLink(shareForm))}`}
                className="h-11 rounded-xl border border-border text-xs font-extrabold flex items-center justify-center gap-1.5 hover:bg-muted"><Mail className="h-3.5 w-3.5" /> Email</a>
            </div>
            <div className="rounded-xl border border-border p-4 text-center" data-testid="share-qr">
              <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-2 flex items-center justify-center gap-1"><QrCode className="h-3.5 w-3.5" /> QR Code</p>
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(formLink(shareForm))}`} alt="Form QR code" className="mx-auto rounded-lg" width={180} height={180} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-1.5 flex items-center gap-1"><Code2 className="h-3.5 w-3.5" /> Embed on Website</p>
              <div className="flex gap-1.5">
                <code className="flex-1 rounded-xl bg-muted/50 px-3 py-2.5 text-[11px] break-all" data-testid="share-embed-code">{`<iframe src="${formLink(shareForm)}" style="width:100%;min-height:640px;border:0" title="${shareForm.name}"></iframe>`}</code>
                <button onClick={() => copy(`<iframe src="${formLink(shareForm)}" style="width:100%;min-height:640px;border:0" title="${shareForm.name}"></iframe>`, "Embed code")} className="h-10 w-10 rounded-lg border border-border flex items-center justify-center hover:bg-muted shrink-0" data-testid="share-embed-copy"><Copy className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Analytics modal */}
      <Modal open={!!analytics} onClose={() => setAnalytics(null)} title={`Analytics — ${analytics?.form?.name || ""}`} testid="form-analytics-modal">
        {analytics && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <StatCard label="Views" value={analytics.views} />
              <StatCard label="Submissions" value={analytics.submissions} />
              <StatCard label="Conversion" value={`${analytics.conversion}%`} />
              <StatCard label="Leads Created" value={analytics.leads_created} />
              <StatCard label="Duplicates" value={analytics.duplicates} />
            </div>
            {analytics.by_date.length > 0 && (
              <div className="h-44"><ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics.by_date}><XAxis dataKey="date" tick={{ fontSize: 9 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={24} /><Tooltip /><Area dataKey="count" stroke="#059669" fill="#05966922" strokeWidth={2} /></AreaChart>
              </ResponsiveContainer></div>
            )}
            <div>
              <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-2">Recent Leads</p>
              {analytics.recent_leads.length === 0 ? <p className="text-sm text-muted-foreground">No leads yet.</p>
                : analytics.recent_leads.map((l) => (
                  <div key={l.id} className="flex justify-between text-sm py-1.5 border-b border-border last:border-0">
                    <span className="truncate font-semibold">{l.name}</span><span className="text-muted-foreground text-xs shrink-0 ml-2">{fmtDateTime(l.created_at)}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
